import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Tooltip from "@mui/material/Tooltip";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";
import LinearProgress from "@mui/material/LinearProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageGuide from "components/admin/PageGuide";
import Footer from "examples/Footer";
import PageCard from "components/admin/PageCard";
import AdminTable from "components/admin/AdminTable";
import ActionDialog from "components/admin/ActionDialog";
import { GiftPreview } from "components/admin/GiftEditor";
import GameIcon from "components/admin/GameIcon";
import BossEditDialog from "layouts/boss/BossEditDialog";
import SummonBossDialog from "layouts/boss/SummonBossDialog";
import GoldenHourTab from "layouts/boss/GoldenHourTab";
import { useConfirm } from "components/admin/Feedback";
import { useFetch, useRunner } from "components/admin/hooks";
import api from "api/client";
import { useAuth } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";
import { formatDate, formatNumber } from "utils/format";

const RESULTS = { KILL: "Bị hạ", TIMEOUT: "Hết giờ", SHUTDOWN: "Máy chủ tắt giữa trận" };
/** Boss thế giới không thuộc khu nào (khu -1) thì chỉ hiện tên map. */
const placeText = (l) => (l.zoneId >= 0 ? `${l.mapName} · khu ${l.zoneId}` : l.mapName);
const RESULT_COLORS = { KILL: "success", TIMEOUT: "warning", SHUTDOWN: "default" };

function LiveTab({ maps }) {
  const canEdit = useAuth().can(PERMISSIONS.BOSS_EDIT);
  const confirm = useConfirm();
  const [run, busy] = useRunner();
  const [mapId, setMapId] = useState(0);
  const [counterTarget, setCounterTarget] = useState(null);
  const [left, setLeft] = useState(1);
  const live = useFetch(() => api.get("/api/admin/boss/live", { mapId }), [mapId], 5000);

  const setCounter = async (zone, position, leftValue) => {
    const res = await run(
      () =>
        api.post("/api/admin/boss/counter", {
          mapId: zone.mapId,
          zoneId: zone.zoneId,
          position,
          left: leftValue,
        }),
      (data) => data.text
    );
    if (res.ok) live.reload(true);
    return res.ok;
  };

  const spawnNow = async (zone, counter) => {
    const ok = await confirm({
      title: "Gọi boss ra ngay",
      message: [
        `${counter.name} sẽ xuất hiện ngay ở ${zone.mapName} khu ${zone.zoneId}`,
        `(đang có ${zone.players} người trong khu).`,
        "",
        "Bộ đếm của boss này về 0 và bắt đầu đếm lại sau khi boss bị hạ hoặc bỏ đi.",
      ].join("\n"),
      color: "warning",
      confirmText: "Gọi ra ngay",
    });
    if (ok) return setCounter(zone, counter.position, 0);
    return false;
  };

  const zones = live.data?.zones || [];
  const rows = zones.flatMap((zone) => [
    ...zone.counters.map((counter) => ({
      zone,
      counter,
      key: `${zone.mapId}-${zone.zoneId}-${counter.position}`,
    })),
    ...(zone.others || []).map((counter, i) => ({
      zone,
      counter,
      other: true,
      key: `${zone.mapId}-${zone.zoneId}-x${i}`,
    })),
  ]);

  return (
    <PageCard
      title="Bộ đếm boss theo khu"
      subtitle="Tự làm mới mỗi 5 giây"
      color="error"
      noPadding
      actions={
        <MDInput
          select
          size="small"
          value={mapId}
          onChange={(e) => setMapId(e.target.value)}
          sx={{ minWidth: 220, background: "white", borderRadius: 1 }}
          SelectProps={{ sx: { height: 36 } }}
        >
          <MenuItem value={0}>Tất cả map có boss</MenuItem>
          {maps
            .filter((m) => m.bosses.length > 0 || m.hasMobs)
            .map((m) => (
              <MenuItem key={m.mapId} value={m.mapId}>
                {m.name}
              </MenuItem>
            ))}
        </MDInput>
      }
    >
      {live.data?.goldenHour?.active && (
        <MDBox px={2}>
          <Alert severity="warning">
            Đang trong giờ vàng: mỗi quái hồi sinh cộng x{live.data.goldenHour.bossCounterMul} vào
            bộ đếm.
          </Alert>
        </MDBox>
      )}
      {live.error && <Alert severity="error">{live.error}</Alert>}
      <AdminTable
        loading={live.loading}
        rows={rows}
        rowKey="key"
        emptyText={
          mapId
            ? "Map này chưa có boss. Muốn gọi boss ra thì vào tab Danh sách boss → Gọi ra."
            : "Không có khu nào có boss"
        }
        columns={[
          {
            key: "zone",
            label: "Map / khu",
            render: (r) => (
              <MDBox lineHeight={1.2}>
                <MDTypography variant="button" fontWeight="medium" display="block">
                  {r.zone.mapName}
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  khu {r.zone.zoneId} · {r.zone.players} người
                </MDTypography>
              </MDBox>
            ),
          },
          {
            key: "boss",
            label: "Boss",
            render: (r) =>
              r.other ? (
                <MDBox lineHeight={1.2}>
                  <MDTypography variant="button" display="block">
                    {r.counter.name}
                  </MDTypography>
                  <Tooltip title="Boss này không ra theo bộ đếm của map: admin gọi ra, hoặc từ sự kiện / nhiệm vụ">
                    <MDTypography variant="caption" color="text">
                      ngoài bộ đếm
                    </MDTypography>
                  </Tooltip>
                </MDBox>
              ) : (
                r.counter.name
              ),
          },
          {
            key: "progress",
            label: "Bộ đếm",
            render: (r) => {
              if (r.counter.alive) {
                const percent = r.counter.maxHp > 0 ? (r.counter.hp * 100) / r.counter.maxHp : 0;
                return (
                  <MDBox minWidth={160}>
                    <MDTypography variant="caption" color="error">
                      Còn {formatNumber(r.counter.hp)} / {formatNumber(r.counter.maxHp)} máu
                    </MDTypography>
                    <LinearProgress variant="determinate" value={percent} color="error" />
                  </MDBox>
                );
              }
              const percent =
                r.counter.need > 0 ? Math.min(100, (r.counter.current * 100) / r.counter.need) : 0;
              return (
                <MDBox minWidth={160}>
                  <MDTypography variant="caption">
                    Quái hồi sinh {formatNumber(r.counter.current)} / {formatNumber(r.counter.need)}
                  </MDTypography>
                  <LinearProgress variant="determinate" value={percent} />
                </MDBox>
              );
            },
          },
          {
            key: "status",
            label: "Trạng thái",
            render: (r) => (
              <MDBox>
                <Chip
                  size="small"
                  color={r.counter.alive ? "error" : "default"}
                  label={r.counter.status}
                />
                {r.counter.timeOut && (
                  <MDTypography variant="caption" display="block">
                    Biến mất lúc {formatDate(r.counter.timeOut)}
                  </MDTypography>
                )}
              </MDBox>
            ),
          },
          canEdit && {
            key: "actions",
            label: "",
            align: "right",
            render: (r) => (
              <MDBox display="flex" gap={1} justifyContent="flex-end">
                <MDButton
                  size="small"
                  variant="outlined"
                  color="info"
                  disabled={busy || r.counter.alive || r.other}
                  onClick={() => {
                    setLeft(Math.max(1, r.counter.need - r.counter.current));
                    setCounterTarget(r);
                  }}
                >
                  Đặt bộ đếm
                </MDButton>
                <MDButton
                  size="small"
                  variant="gradient"
                  color="error"
                  disabled={busy || r.counter.alive || r.other}
                  onClick={() => spawnNow(r.zone, r.counter)}
                >
                  Gọi ngay
                </MDButton>
              </MDBox>
            ),
          },
        ].filter(Boolean)}
      />
      {counterTarget && (
        <ActionDialog
          open
          title={`Đặt bộ đếm: ${counterTarget.counter.name}`}
          description="Mỗi lần một con quái trong khu hồi sinh, bộ đếm tăng 1 (giờ vàng tăng nhiều hơn). Đủ số thì boss xuất hiện. Đặt số còn thiếu nhỏ lại để boss ra sớm hơn."
          submitText={Number(left) === 0 ? "Gọi ra ngay…" : "Lưu bộ đếm"}
          busy={busy}
          disabled={left === "" || Number(left) < 0}
          onClose={() => setCounterTarget(null)}
          onSubmit={async () => {
            const done =
              Number(left) === 0
                ? await spawnNow(counterTarget.zone, counterTarget.counter)
                : await setCounter(
                    counterTarget.zone,
                    counterTarget.counter.position,
                    Number(left)
                  );
            if (done) setCounterTarget(null);
          }}
        >
          <MDTypography variant="button" color="text">
            {counterTarget.zone.mapName} khu {counterTarget.zone.zoneId}: đã có{" "}
            {formatNumber(counterTarget.counter.current)} /{" "}
            {formatNumber(counterTarget.counter.need)} quái hồi sinh, còn thiếu{" "}
            <b>{formatNumber(counterTarget.counter.need - counterTarget.counter.current)}</b>.
          </MDTypography>
          <MDInput
            type="number"
            label="Đặt số quái còn thiếu thành"
            value={left}
            inputProps={{ min: 0, max: counterTarget.counter.need }}
            onChange={(e) => setLeft(e.target.value)}
            helperText={
              Number(left) === 0
                ? "0 = gọi boss ra ngay"
                : `Boss ra sau khi thêm ${formatNumber(Number(left) || 0)} con quái hồi sinh`
            }
            autoFocus
          />
          <MDBox display="flex" gap={1} flexWrap="wrap">
            {[0, 10, 50, 100]
              .filter((n) => n <= counterTarget.counter.need)
              .map((n) => (
                <Chip
                  key={n}
                  size="small"
                  variant="outlined"
                  label={n === 0 ? "Ra ngay" : `Còn ${n}`}
                  onClick={() => setLeft(n)}
                />
              ))}
          </MDBox>
        </ActionDialog>
      )}
    </PageCard>
  );
}

LiveTab.propTypes = { maps: PropTypes.arrayOf(PropTypes.object).isRequired };

function LogDetailDialog({ id, onClose }) {
  const { data, loading, error } = useFetch(() => api.get(`/api/admin/boss/logs/${id}`), [id]);
  const log = data?.log;
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Trận #{id} {log ? `- ${log.bossName}` : ""}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        {log && (
          <MDBox mb={2}>
            <MDTypography variant="button" display="block">
              {placeText(log)} · {formatDate(log.createTime)} · Máu {formatNumber(log.maxHp)}
            </MDTypography>
            <MDTypography variant="button" display="block">
              Kết quả: {RESULTS[log.result] || log.result}
              {log.killer && ` · Kết liễu: ${log.killer.name}`}
              {log.lottery && ` · Trúng bốc thăm: ${log.lottery.name}`}
            </MDTypography>
          </MDBox>
        )}
        <AdminTable
          loading={loading}
          rows={data?.players || []}
          rowKey="userId"
          emptyText="Không có người tham gia"
          columns={[
            { key: "rank", label: "Hạng", render: (p) => (p.rank > 0 ? p.rank : "-") },
            {
              key: "name",
              label: "Nhân vật",
              render: (p) => <Link to={`/players/${p.userId}`}>{p.name}</Link>,
            },
            {
              key: "damage",
              label: "Sát thương",
              align: "right",
              render: (p) => formatNumber(p.damage),
            },
            { key: "ticket", label: "Vé bốc thăm", align: "right" },
          ]}
        />
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onClose}>
          Đóng
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

LogDetailDialog.propTypes = { id: PropTypes.number.isRequired, onClose: PropTypes.func.isRequired };

function LogsTab({ bosses }) {
  const [filter, setFilter] = useState({ name: "", bossId: 0, result: "" });
  const [applied, setApplied] = useState(filter);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [detailId, setDetailId] = useState(null);
  const { data, loading, error } = useFetch(
    () => api.get("/api/admin/boss/logs", { ...applied, page, size }),
    [applied, page, size]
  );

  return (
    <PageCard
      title="Nhật ký boss"
      subtitle="Mỗi dòng là một lần boss xuất hiện. Bấm vào dòng để xem ai đánh bao nhiêu."
      color="dark"
      noPadding
    >
      <MDBox
        px={2}
        pb={1}
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          setApplied({ ...filter });
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <MDInput
              fullWidth
              label="Tên nhân vật tham gia"
              value={filter.name}
              onChange={(e) => setFilter({ ...filter, name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <MDInput
              select
              fullWidth
              label="Boss"
              value={filter.bossId}
              onChange={(e) => setFilter({ ...filter, bossId: e.target.value })}
              SelectProps={{ sx: { height: 44 } }}
            >
              <MenuItem value={0}>Tất cả</MenuItem>
              {bosses.map((b) => (
                <MenuItem key={b.bossId} value={b.bossId}>
                  {b.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} md={2}>
            <MDInput
              select
              fullWidth
              label="Kết quả"
              value={filter.result}
              onChange={(e) => setFilter({ ...filter, result: e.target.value })}
              SelectProps={{ sx: { height: 44 } }}
            >
              <MenuItem value="">Tất cả</MenuItem>
              {Object.entries(RESULTS).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs="auto">
            <MDButton type="submit" variant="gradient" color="info">
              Lọc
            </MDButton>
          </Grid>
        </Grid>
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
      </MDBox>
      <AdminTable
        loading={loading}
        rows={data?.items || []}
        page={page}
        size={size}
        total={data?.total ?? 0}
        onPageChange={setPage}
        onSizeChange={(s) => {
          setSize(s);
          setPage(1);
        }}
        onRowClick={(row) => setDetailId(row.id)}
        columns={[
          { key: "createTime", label: "Lúc", render: (l) => formatDate(l.createTime) },
          { key: "bossName", label: "Boss" },
          { key: "map", label: "Ở đâu", render: (l) => placeText(l) },
          {
            key: "result",
            label: "Kết quả",
            render: (l) => (
              <Chip
                size="small"
                color={RESULT_COLORS[l.result] || "default"}
                label={RESULTS[l.result] || l.result}
              />
            ),
          },
          { key: "killer", label: "Người kết liễu", render: (l) => l.killer?.name || "-" },
          {
            key: "top",
            label: "Top sát thương",
            wrap: true,
            render: (l) => l.top.map((p) => p.name).join(", ") || "-",
          },
          { key: "lottery", label: "Trúng bốc thăm", render: (l) => l.lottery?.name || "-" },
          { key: "participantCount", label: "Số người đánh", align: "right" },
          applied.name && {
            key: "damage",
            label: "Sát thương",
            align: "right",
            render: (l) => formatNumber(l.damage),
          },
        ].filter(Boolean)}
      />
      {detailId && <LogDetailDialog id={detailId} onClose={() => setDetailId(null)} />}
    </PageCard>
  );
}

LogsTab.propTypes = { bosses: PropTypes.arrayOf(PropTypes.object).isRequired };

function BossListTab({ bosses, maps, loading, onChanged }) {
  const canEdit = useAuth().can(PERMISSIONS.BOSS_EDIT);
  const [editing, setEditing] = useState(null);
  const [summoning, setSummoning] = useState(null);
  return (
    <PageCard
      title="Danh sách boss"
      subtitle="Sửa chỉ số / quà: boss ra sau dùng số mới, boss đang có giữ số cũ"
      color="secondary"
      noPadding
    >
      <AdminTable
        loading={loading}
        rows={bosses}
        rowKey="bossId"
        columns={[
          {
            key: "name",
            label: "Boss",
            render: (b) => (
              <MDBox display="flex" alignItems="center" gap={1}>
                <GameIcon path={b.petIcon} size={32} fallback="pets" />
                <MDBox lineHeight={1.2}>
                  {b.name}
                  <MDTypography variant="caption" color="text" display="block">
                    #{b.bossId}
                  </MDTypography>
                  {b.alive > 0 && (
                    <MDBox>
                      <Chip size="small" color="error" label={`${b.alive} con đang sống`} />
                    </MDBox>
                  )}
                </MDBox>
              </MDBox>
            ),
          },
          { key: "lvl", label: "Cấp", align: "right" },
          { key: "hp", label: "HP", align: "right", render: (b) => formatNumber(b.hp) },
          { key: "atk", label: "Công", align: "right", render: (b) => formatNumber(b.atk) },
          { key: "exp", label: "Kinh nghiệm", align: "right", render: (b) => formatNumber(b.exp) },
          {
            key: "maps",
            label: "Xuất hiện ở",
            wrap: true,
            render: (b) => b.maps.map((m) => m.name).join(", ") || "-",
          },
          {
            key: "gift",
            label: "Quà top / bốc thăm / kết liễu",
            wrap: true,
            render: (b) => <GiftPreview lines={b.giftInfo || []} />,
          },
          {
            key: "giftJoin",
            label: "Quà tham gia",
            wrap: true,
            render: (b) => <GiftPreview lines={b.giftJoinInfo || []} />,
          },
          canEdit && {
            key: "actions",
            label: "",
            align: "right",
            render: (b) => (
              <MDBox display="flex" justifyContent="flex-end">
                <MDButton size="small" variant="text" color="warning" onClick={() => setEditing(b)}>
                  <Icon>edit</Icon>&nbsp;Sửa
                </MDButton>
                <MDButton size="small" variant="text" color="error" onClick={() => setSummoning(b)}>
                  <Icon>bolt</Icon>&nbsp;Gọi ra
                </MDButton>
              </MDBox>
            ),
          },
        ].filter(Boolean)}
      />
      {editing && (
        <BossEditDialog boss={editing} onClose={() => setEditing(null)} onDone={onChanged} />
      )}
      {summoning && (
        <SummonBossDialog
          boss={summoning}
          maps={maps}
          onClose={() => setSummoning(null)}
          onDone={onChanged}
        />
      )}
    </PageCard>
  );
}

BossListTab.propTypes = {
  bosses: PropTypes.arrayOf(PropTypes.object).isRequired,
  maps: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool.isRequired,
  onChanged: PropTypes.func.isRequired,
};

// Tab ghi lên URL (?tab=) để gửi link thẳng tới một tab.
const TABS = [
  { key: "live", label: "Đang diễn ra" },
  { key: "logs", label: "Nhật ký" },
  { key: "golden", label: "Giờ vàng" },
  { key: "list", label: "Danh sách boss" },
];
const TAB_SX = { flex: { xs: "0 0 auto", md: "1 1 auto" }, px: 1.5, whiteSpace: "nowrap" };

function Boss() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "live";
  const setTab = (v) => setParams(v === "live" ? {} : { tab: v }, { replace: true });
  const maps = useFetch(() => api.get("/api/admin/templates/maps"), []);
  const bosses = useFetch(() => api.get("/api/admin/templates/bosses"), []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <PageGuide page="boss" />
        <Card sx={{ mb: 3 }}>
          <Tabs
            value={tab}
            onChange={(e, value) => setTab(value)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {TABS.map((t) => (
              <Tab key={t.key} value={t.key} label={t.label} sx={TAB_SX} />
            ))}
          </Tabs>
        </Card>
        {tab === "live" && <LiveTab maps={maps.data || []} />}
        {tab === "logs" && <LogsTab bosses={bosses.data || []} />}
        {tab === "golden" && <GoldenHourTab />}
        {tab === "list" && (
          <BossListTab
            bosses={bosses.data || []}
            maps={maps.data || []}
            loading={bosses.loading}
            onChanged={() => bosses.reload(true)}
          />
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Boss;
